import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, TriangleAlert, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog } from '@/components/ui/dialog';
import { Field, Textarea } from '@/components/ui/field';
import { apiError, type Plan } from '../lib';
import {
  MARKETING_LOCALES,
  ceilingEchoes,
  listPriceProblem,
  marketingProblems,
  toForm,
  toPayload,
  type MarketingLocale,
  type PlanMarketing,
} from './marketing';

/**
 * The words that go on this plan's card on tarabeeza.com.
 *
 * BOTH LANGUAGES ON ONE SCREEN, field for field, for the same reason the site
 * content editor does it: a translator has to see the English line to write the
 * Arabic one, and the public page renders the two independently with no
 * fallback between them. A plan with English copy and no Arabic does not render
 * an English card to an Arabic reader — it renders no card at all, and the rate
 * card silently says something different to each half of the audience.
 *
 * Save is therefore blocked until both are complete, and the incomplete fields
 * are named. The API refuses the same shapes with the same paths; this is the
 * copy of the rule that answers while somebody is still typing.
 */
export default function PlanMarketingDialog({
  plan,
  onClose,
  onSaved,
}: {
  plan: Plan | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState<PlanMarketing>(() => toForm(plan?.marketing));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(toForm(plan?.marketing));
  }, [plan]);

  const price = plan?.priceMonthly ?? '';
  const problems = useMemo(() => marketingProblems(form, price), [form, price]);
  const echoes = useMemo(() => ceilingEchoes(form), [form]);
  const listPrice = useMemo(() => listPriceProblem(form, price), [form, price]);

  if (!plan) return null;

  const edit = (locale: MarketingLocale, patch: Partial<PlanMarketing[MarketingLocale]>) =>
    setForm((current) => ({ ...current, [locale]: { ...current[locale], ...patch } }));

  /* A feature is added to BOTH languages at once. Letting the lists drift apart
     would put six bullets on one card and five on the other. */
  const addFeature = () =>
    setForm((current) => ({
      ...current,
      en: { ...current.en, features: [...current.en.features, ''] },
      ar: { ...current.ar, features: [...current.ar.features, ''] },
    }));

  const removeFeature = (index: number) =>
    setForm((current) => ({
      ...current,
      en: { ...current.en, features: current.en.features.filter((_, i) => i !== index) },
      ar: { ...current.ar, features: current.ar.features.filter((_, i) => i !== index) },
    }));

  const rows = Math.max(form.en.features.length, form.ar.features.length);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/subscriptions/plans/${plan.id}`, { marketing: toPayload(form) });
      toast.success(t('platform.plans.marketing.saved'));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('platform.plans.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('platform.plans.marketing.title', { name: plan.name })}
      description={t('platform.plans.marketing.hint')}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => void save()} disabled={saving || problems.length > 0}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* The price, the currency and the ceilings are NOT edited here. They
            are the plan's own columns, edited above, and the card reads them
            from the row so the page and the invoice cannot disagree. */}
        <p className="rounded-md border border-[var(--border)] bg-[var(--muted)] p-3 text-xs text-[var(--muted-foreground)]">
          {t('platform.plans.marketing.figuresNote')}
        </p>

        {problems.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-red-900">
            <p className="text-sm font-semibold">{t('platform.plans.marketing.incomplete')}</p>
            <p className="mt-1 text-xs">{t('platform.plans.marketing.incompleteBody')}</p>
            <code className="mt-2 block break-all text-xs" dir="ltr">
              {problems.join(' · ')}
            </code>
          </div>
        )}

        {echoes.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="text-sm font-semibold">{t('platform.plans.marketing.echoTitle')}</p>
              <p className="mt-1 text-xs">{t('platform.plans.marketing.echoBody')}</p>
              <code className="mt-2 block break-all text-xs" dir="ltr">
                {echoes.join(' · ')}
              </code>
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={form.featured === true}
            onChange={(e) => setForm({ ...form, featured: e.target.checked })}
          />
          {t('platform.plans.marketing.featured')}
        </label>

        {/*
          The launch offer: a price the card strikes through, and the line that
          says what the offer is.

          The struck-through figure is the only price typed on this screen, and
          it is the only one nothing bills against — which is exactly why it is
          checked here against the plan's real price rather than left to the
          server. A "was" price at or below what a tenant pays is not a smaller
          discount, it is a false statement printed beside the true one, and the
          public page refuses to draw it. Better to say so with the real price
          on screen than to have the plan quietly vanish from the pricing band.
        */}
        <div className="space-y-3 rounded-lg border border-[var(--border)] p-3">
          <div>
            <p className="text-xs font-medium">{t('platform.plans.marketing.offer.title')}</p>
            <p className="mt-1 text-xs text-[var(--muted-foreground)]">
              {t('platform.plans.marketing.offer.hint', {
                price: formatCurrency(plan.priceMonthly, plan.currency, i18n.language),
              })}
            </p>
          </div>

          <Field
            label={t('platform.plans.marketing.offer.listPrice')}
            error={
              listPrice
                ? t(`platform.plans.marketing.offer.error.${listPrice}`, {
                    price: formatCurrency(plan.priceMonthly, plan.currency, i18n.language),
                  })
                : undefined
            }
            hint={t('platform.plans.marketing.offer.listPriceHint')}
          >
            <Input
              value={form.listPriceMonthly ?? ''}
              dir="ltr"
              inputMode="decimal"
              placeholder="398.00"
              aria-label={t('platform.plans.marketing.offer.listPrice')}
              onChange={(e) => setForm({ ...form, listPriceMonthly: e.target.value })}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            {MARKETING_LOCALES.map((locale) => (
              <Field
                key={locale}
                label={t('platform.plans.marketing.offer.note')}
                hint={t(`platform.content.locale.${locale}`)}
              >
                <Input
                  value={form.offer?.[locale] ?? ''}
                  dir={locale === 'ar' ? 'rtl' : 'ltr'}
                  lang={locale}
                  aria-label={`offer.${locale}`}
                  placeholder={t('platform.plans.marketing.offer.notePlaceholder')}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      offer: {
                        en: form.offer?.en ?? '',
                        ar: form.offer?.ar ?? '',
                        [locale]: e.target.value,
                      },
                    })
                  }
                />
              </Field>
            ))}
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {MARKETING_LOCALES.map((locale) => (
            <Field key={locale} label={t(`platform.content.locale.${locale}`)} required>
              <Input
                value={form[locale].name}
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
                lang={locale}
                placeholder={t('platform.plans.marketing.namePlaceholder')}
                onChange={(e) => edit(locale, { name: e.target.value })}
              />
            </Field>
          ))}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          {MARKETING_LOCALES.map((locale) => (
            <Field
              key={locale}
              label={t('platform.plans.marketing.audience')}
              hint={t(`platform.content.locale.${locale}`)}
              required
            >
              <Textarea
                rows={2}
                value={form[locale].audience}
                dir={locale === 'ar' ? 'rtl' : 'ltr'}
                lang={locale}
                onChange={(e) => edit(locale, { audience: e.target.value })}
              />
            </Field>
          ))}
        </div>

        <div className="space-y-3">
          <p className="text-xs font-medium text-[var(--muted-foreground)]">
            {t('platform.plans.marketing.features')}
          </p>
          {Array.from({ length: rows }, (_, index) => (
            <div key={index} className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="eyebrow text-[var(--muted-foreground)]">
                  {t('platform.content.item', { n: index + 1 })}
                </span>
                {rows > 1 && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => removeFeature(index)}
                    aria-label={t('platform.content.removeItem')}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {MARKETING_LOCALES.map((locale) => (
                  <Input
                    key={locale}
                    value={form[locale].features[index] ?? ''}
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                    lang={locale}
                    aria-label={`${locale}.features[${index}]`}
                    onChange={(e) => {
                      const features = [...form[locale].features];
                      while (features.length <= index) features.push('');
                      features[index] = e.target.value;
                      edit(locale, { features });
                    }}
                  />
                ))}
              </div>
            </div>
          ))}
          <Button size="sm" variant="secondary" onClick={addFeature}>
            <Plus className="h-4 w-4" />
            {t('platform.content.addItem')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
